import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, Search } from "lucide-react";
import IntelligenceTopBar from "@/components/IntelligenceTopBar";
import { useAuth } from "@/contexts/AuthContext";
import { fetchArchivedSessions, formatArchiveTimestamp, type ArchivedSession } from "@/lib/sessionArchive";
import { Input } from "@/components/ui/input";

const RehearsalArchive = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sessions, setSessions] = useState<ArchivedSession[] | null>(null);
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!user) return;
    fetchArchivedSessions(user.id, 60).then(setSessions).catch(() => setSessions([]));
  }, [user]);

  const filtered = useMemo(() => {
    if (!sessions) return [];
    if (!query.trim()) return sessions;
    const q = query.toLowerCase();
    return sessions.filter(
      (s) =>
        s.archetypeName.toLowerCase().includes(q) ||
        s.scenarioLabel.toLowerCase().includes(q) ||
        s.observation.toLowerCase().includes(q)
    );
  }, [sessions, query]);

  const recurringCount = useMemo(
    () => (sessions || []).filter((s) => s.recurring).length,
    [sessions]
  );

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ background: "linear-gradient(180deg, hsl(0 0% 7%) 0%, hsl(0 0% 3%) 100%)" }}
    >
      <IntelligenceTopBar />

      <div className="flex-1 max-w-3xl mx-auto w-full px-4 pt-12 md:pt-16 pb-32">
        {/* Header */}
        <button
          onClick={() => navigate("/practice")}
          className="inline-flex items-center gap-1.5 text-[11px] font-mono tracking-[0.22em] uppercase text-muted-foreground/60 hover:text-cyan transition-colors mb-8"
        >
          <ArrowLeft className="w-3 h-3" /> Practice
        </button>

        <div className="space-y-3 mb-12">
          <div className="inline-flex items-center gap-2">
            <span className="h-px w-6 bg-cyan/40" />
            <span className="text-[10px] font-mono tracking-[0.32em] uppercase text-cyan/70">
              Behavioral Records
            </span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight">
            <span className="text-foreground">Archived </span>
            <span className="text-gradient-cyan">Sessions</span>
          </h1>
          <p className="text-sm text-muted-foreground/75 max-w-xl leading-relaxed">
            A quiet record of behavioral exchanges you've rehearsed. Each entry preserves the
            observation — not the conversation.
          </p>
        </div>

        {/* Memory hook */}
        {sessions && recurringCount > 0 && (
          <div className="mb-10">
            <div className="rounded-xl border border-cyan/15 bg-cyan/[0.03] px-4 py-3">
              <p className="text-[9px] font-mono tracking-[0.32em] uppercase text-cyan/70 mb-1">
                Pattern Memory
              </p>
              <p className="text-sm text-foreground/75 italic font-light leading-relaxed">
                {recurringCount === 1
                  ? "This pattern has appeared before."
                  : `${recurringCount} recurring archetypes observed across your sessions.`}
              </p>
            </div>
          </div>
        )}

        {/* Search */}
        {sessions && sessions.length > 5 && (
          <div className="relative mb-6">
            <Search className="w-3.5 h-3.5 text-muted-foreground/50 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Filter by archetype, scenario, observation…"
              className="pl-9 h-10 bg-secondary/30 border-border/50 text-sm placeholder:text-muted-foreground/40"
            />
          </div>
        )}

        {/* Body */}
        {sessions === null ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-5 h-5 text-cyan/70 animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="rounded-2xl border border-border/40 bg-card/30 px-6 py-14 text-center">
            <p className="text-[10px] font-mono tracking-[0.28em] uppercase text-muted-foreground/45 mb-3">
              {sessions.length === 0 ? "Archive Empty" : "No Matches"}
            </p>
            <p className="text-sm text-foreground/55 italic font-light max-w-sm mx-auto leading-relaxed">
              {sessions.length === 0
                ? "Completed rehearsals will preserve themselves here as forensic behavioral records."
                : "Try a different search term."}
            </p>
            {sessions.length === 0 && (
              <button
                onClick={() => navigate("/practice")}
                className="mt-6 text-[11px] font-mono tracking-[0.25em] uppercase text-cyan/80 hover:text-cyan transition-colors"
              >
                Begin a Rehearsal →
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((s) => (
              <button
                key={s.id}
                onClick={() => navigate(`/rehearsal/debrief/${s.id}`)}
                className="w-full text-left rounded-2xl border border-border/40 bg-card/40 hover:bg-card/70 hover:border-cyan/30 hover:shadow-[0_0_40px_-16px_hsl(184_100%_50%/0.5)] transition-all duration-500 group overflow-hidden"
              >
                {/* Top metadata strip */}
                <div className="flex items-center justify-between px-5 pt-4">
                  <span className="text-[9px] font-mono tracking-[0.3em] uppercase text-cyan/60">
                    Session Record
                  </span>
                  <span className="text-[10px] font-mono tracking-[0.2em] text-muted-foreground/45">
                    {formatArchiveTimestamp(s.createdAt)}
                  </span>
                </div>

                <div className="px-5 pt-3 pb-5 space-y-3">
                  <div className="flex items-baseline gap-3 flex-wrap">
                    <h3 className="text-lg md:text-xl font-semibold text-foreground tracking-tight">
                      {s.archetypeName}
                    </h3>
                    {s.recurring && (
                      <span className="text-[9px] font-mono tracking-[0.24em] uppercase text-cyan/70 border border-cyan/30 rounded-sm px-1.5 py-[2px]">
                        Recurring
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] font-mono tracking-[0.18em] uppercase text-muted-foreground/60">
                    {s.scenarioLabel}
                  </div>

                  <p className="text-sm text-foreground/75 font-light leading-relaxed pt-1 border-l border-cyan/30 pl-3">
                    {s.observation}
                  </p>

                  {s.resonantPreview && (
                    <p className="text-[12px] text-foreground/45 italic font-light leading-relaxed pt-1">
                      {s.resonantPreview}
                    </p>
                  )}

                  <div className="pt-2 flex items-center gap-1.5 text-[10px] font-mono tracking-[0.22em] uppercase text-cyan/60 group-hover:text-cyan transition-colors">
                    Return to Debrief →
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default RehearsalArchive;
